import { useQuery } from '@apollo/client';
import { createError } from '@common/neo.tax';

import {
  GET_STATE_DOCUMENT_STATUS,
  GetStateDocumentStatusQuery,
  GetStateDocumentStatusQueryVariables,
} from '@neo.tax/shared';

type GetStateDocumentStatusType =
  | {
      stateDocumentStatus: boolean;
      loading: false;
    }
  | {
      stateDocumentStatus: null;
      loading: true;
    };

type GetStateDocumentStatusTypeWithSkip =
  | {
      stateDocumentStatus: null;
      loading: false;
    }
  | GetStateDocumentStatusType;

export interface UseGetStateDocumentStatusOptions {
  skip?: boolean;
  fiscalYearId?: string;
}

export const useGetStateDocumentStatus = ({
  fiscalYearId,
  skip,
}: UseGetStateDocumentStatusOptions): GetStateDocumentStatusTypeWithSkip => {
  const shouldSkip = skip || !fiscalYearId;
  const { data, error, loading } = useQuery<GetStateDocumentStatusQuery, GetStateDocumentStatusQueryVariables>(
    GET_STATE_DOCUMENT_STATUS,
    {
      variables: { fiscalYearId: fiscalYearId! },
      skip: shouldSkip,
    },
  );

  if (shouldSkip) {
    return { stateDocumentStatus: null, loading: false };
  }

  if (loading) {
    return { stateDocumentStatus: null, loading: true };
  }

  if (error) {
    throw error;
  }

  if (!data) {
    throw createError('MissingStateDocumentStatus', 'Failed to load state document status');
  }

  return {
    stateDocumentStatus: data?.getStateDocumentStatus,
    loading,
  };
};
